import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import './DraftBoard.css';

const DraftBoard = ({ draftPicks, entrants, contestFormat }) => {
  // Memoize calculations to avoid re-running on every render unless data changes
  const { rounds, picksByRoundAndUser } = useMemo(() => {
    if (!entrants || entrants.length === 0) {
      return { rounds: 0, picksByRoundAndUser: new Map() };
    }

    const match = contestFormat.match(/(\d+)/);
    const numRounds = match ? parseInt(match[1], 10) : 0;
    const map = new Map();

    // The backend now sends a flat array of picks.
    // We need to map them for easy lookup.
    draftPicks.forEach(pick => {
      const key = `${pick.round}-${pick.pickedBy.userId}`;
      map.set(key, pick.player.name);
    });

    return { rounds: numRounds, picksByRoundAndUser: map };
  }, [draftPicks, entrants, contestFormat]);
  
  const roundNumbers = Array.from({ length: rounds }, (_, i) => i + 1);

  // Function to get the display name for a pick
  const getPickDisplayName = (roundNum, userId) => {
    const pickKey = `${roundNum}-${userId}`;
    const playerName = picksByRoundAndUser.get(pickKey);
    if (!playerName) return '-';
    // Add (CPT) if it's the first pick for this user
    const isFirstPick = roundNum === 1;
    return isFirstPick ? `${playerName} (CPT)` : playerName;
  };

  return (
    <div className="draft-board-container">
      <h4 className="draft-board-title">DRAFT BOARD</h4>
      <div className="draft-board-table-wrapper">
        <table className="draft-board-table">
          <thead>
            <tr>
              <th>RD</th>
              {entrants.map(entrant => (
                <th key={entrant.userId}>{entrant.username}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {roundNumbers.map(roundNum => (
              <tr key={roundNum}>
                <td>{roundNum}</td>
                {entrants.map(entrant => (
                  <td key={entrant.userId}>
                    {getPickDisplayName(roundNum, entrant.userId)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

DraftBoard.propTypes = {
  draftPicks: PropTypes.arrayOf(PropTypes.shape({
    round: PropTypes.number,
    pickedBy: PropTypes.shape({ userId: PropTypes.string }),
    player: PropTypes.shape({ name: PropTypes.string }),
  })).isRequired,
  entrants: PropTypes.arrayOf(PropTypes.shape({
    userId: PropTypes.string,
    username: PropTypes.string,
  })).isRequired,
  contestFormat: PropTypes.string.isRequired,
};

export default DraftBoard; 